/** Shared `{ value, label }` shape every serialized enum comes back as. */
export type LabeledEnumPayload = {
	readonly value: string;
	readonly label: string;
};

/** The enum comes back as an object, never a bare string: the screen reads `.label`, the logic reads `.value`. */
export type AiUsageValuePayload = LabeledEnumPayload;

/** Microsoft settings of an Azure Speech model. `phraseWeight` `null` means "Microsoft's own weight". */
export type AzureSpeechSettingsPayload = {
	readonly locale: string;
	readonly maxSpeakers: number;
	readonly profanityFilter: boolean;
	readonly phrases: string[];
	readonly phraseWeight: number | null;
};

// `usages` is a plain array column, not a `@Enum`: no serializer applies to it, unlike `lastTestedUsage`
// below. It comes back as bare strings (`['conversation', 'dictation']`), and the screen resolves their
// labels from the `ai-usages` catalogue it loads anyway. Same precedent as `Role.permissions`
// (`Application/Roles/Actions/rolesFetched.ts`): reading `.label`/`.value` on an entry of this array
// would read `undefined`.
export type AiModelPayload = {
	readonly id: number;
	readonly identifier: string;
	readonly label: string;
	readonly usages: string[];
	readonly lastTestedAt: string | null;
	readonly lastTestSucceeded: boolean | null;
	readonly lastTestMessage: string | null;
	readonly lastTestedUsage: AiUsageValuePayload | null;
	/** `null` means "no override": the provider's own `apiVersion` applies. Only meaningful when the
	 * model's provider is `azure_openai` — every other kind always answers `null`. */
	readonly apiVersion: string | null;
	/** A real nullable enum column: comes back as `{ value, label }` or `null`, never a bare string. */
	readonly family: LabeledEnumPayload | null;
	/** Nullable enum column: `{ value, label }` for an audio model, `null` otherwise. */
	readonly deliveryMode: LabeledEnumPayload | null;
	/** French labels of the settings the last connection test rejected. `null` until a test has ever
	 * run, distinct from the empty array a nominal test always carries on `rejectedSettings` below. */
	readonly lastTestRejectedSettings: string[] | null;
	readonly providerOptions: Record<string, unknown> | null;
	/** Whether the audio is cut into chunks before transcription. */
	readonly transcriptionChunking: boolean;
	/** `null` means "the server's own value applies". */
	readonly transcriptionChunkMaxSeconds: number | null;
	/** `null` means "the server's own value applies". */
	readonly transcriptionConcurrency: number | null;
	/** Euro prices, `null` until set. */
	readonly inputPricePerMillionTokens: number | null;
	readonly outputPricePerMillionTokens: number | null;
	readonly cachedInputPricePerMillionTokens: number | null;
	readonly cacheWritePricePerMillionTokens: number | null;
	readonly pricePerAudioMinute: number | null;
	/** Whether an audio minute already started is billed in full. */
	readonly billedPerStartedMinute: boolean;
	/** `null` outside the Azure Speech family. */
	readonly azureSpeechSettings: AzureSpeechSettingsPayload | null;
	readonly provider: { readonly id: number };
	readonly account: { readonly id: number };
};

export type AiModelsFetchedPayload = AiModelPayload[];

export type AiModelsFetchedAction = {
	type: 'AI_MODELS_FETCHED';
	payload: AiModelsFetchedPayload;
	accountId: number;
};

export default (payload: AiModelsFetchedPayload, accountId: number): AiModelsFetchedAction => ({
	type: 'AI_MODELS_FETCHED',
	payload,
	accountId,
});
